import { Card, Divider, List, ListItem, Stack } from '@mui/material'
import { Icon } from '@mxenabled/mx-icons'

import { Drawer } from '../../../../common'
import { Text } from '@mxenabled/mxui'
import { BalanceStepper } from './BalanceStepper'
import SaveEnoughToLiveOnBottomSection from '../SaveEnoughToLiveOnBottomSection'
import React from 'react'
import { formatCurrency } from '../../../../core/utils/NumberFormatting'
import { buildDynamicCopy } from '../../../../core/utils/Localization'

export const variants = {
  SAVE_ENOUGH_TO_LIVE_ON: 'SaveEnoughToLiveOn',
  SAVE_AN_EXTRA_100: 'SaveAnExtra100',
}

const MONTHS_TABLE_SAVE_ENOUGH = [
  { label: '1 month', value: 1 },
  { label: '3 months', value: 3 },
  { label: '6 months', value: 6 },
  { label: '9 months', value: 9 },
] as const

const MONTHS_TABLE_SAVE_EXTRA = [
  { label: '3 months', value: 3 },
  { label: '6 months', value: 6 },
  { label: '9 months', value: 9 },
] as const

interface CopyContent {
  header_content?: string
  description?: string
  body_content?: string
  body_description?: string
  available_text?: string
  total_remaining_text?: string
  increment_arial_label?: string
  decrement_arial_label?: string
  expenses_text?: string
  available_balances_text?: string
  average_monthly_spend_text?: string
  your_available_balances_text?: string
}

interface DrillDownCalculationProps {
  title: string
  open: boolean
  onClose: () => void
  date?: string
  variant: (typeof variants)[keyof typeof variants]
  svgDateContent?: string
  availableBalances?: number
  copy: CopyContent
  averageMonthlySpend?: number
}

export const DrillDownCalculation = ({
  title,
  open,
  onClose,
  variant = variants.SAVE_ENOUGH_TO_LIVE_ON,
  svgDateContent,
  date,
  copy,
  availableBalances,
  averageMonthlySpend,
}: DrillDownCalculationProps) => {
  const [amountToCalculate, setAmountToCalculate] = React.useState(
    variant === variants.SAVE_AN_EXTRA_100 ? 100 : (averageMonthlySpend ?? 0),
  )

  const monthsTable =
    variant === variants.SAVE_AN_EXTRA_100 ? MONTHS_TABLE_SAVE_EXTRA : MONTHS_TABLE_SAVE_ENOUGH

  const deltaAmount = variant === variants.SAVE_AN_EXTRA_100 ? 5 : 50

  return (
    <Drawer isOpen={open} onClose={onClose} title={title}>
      <Stack
        sx={{
          bgcolor: 'background.paper',
          p: 24,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {variant === variants.SAVE_ENOUGH_TO_LIVE_ON &&
          availableBalances &&
          averageMonthlySpend &&
          svgDateContent && (
            <SaveEnoughToLiveOnBottomSection
              content={svgDateContent || ''}
              leftLabel={copy.available_balances_text!}
              leftValue={formatCurrency(availableBalances, '0,0.00')}
              rightLabel={copy.average_monthly_spend_text!}
              rightValue={formatCurrency(averageMonthlySpend, '0,0.00')}
              sx={{ width: '100%', mb: 24 }}
            />
          )}
        <Stack
          sx={{
            gap: 8,
          }}
        >
          <Text variant="h3">{copy.header_content}</Text>
          <Text sx={{ lineHeight: '24px' }} truncate={false} variant="body1">
            {copy.description}
          </Text>
        </Stack>
      </Stack>
      {variant === variants.SAVE_AN_EXTRA_100 && availableBalances && averageMonthlySpend && (
        <Stack sx={{ pt: 24, px: 24 }}>
          <Card sx={{ px: 16, pt: 16, pb: 12 }}>
            <Stack
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '4px',
                gap: 12,
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 8,
                mb: 12,
              }}
            >
              <Stack sx={{ width: 155, textAlign: 'center' }}>
                <Text sx={{ fontWeight: 600 }} variant="body1">
                  {formatCurrency(availableBalances, '0,0.00')}
                </Text>
                <Text sx={{ color: 'text.secondary' }} variant="caption">
                  {copy.available_text}
                </Text>
              </Stack>
              <Icon name="remove" size={20} />
              <Stack sx={{ width: 155, textAlign: 'center' }}>
                <Text sx={{ fontWeight: 600 }} variant="body1">
                  {formatCurrency(averageMonthlySpend, '0,0.00')}
                </Text>
                <Text sx={{ color: 'text.secondary' }} variant="caption">
                  {copy.expenses_text}
                </Text>
              </Stack>
            </Stack>
            <Stack
              sx={{
                textAlign: 'center',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Text sx={{ color: 'text.secondary' }} variant="body2">
                {copy.total_remaining_text}:
              </Text>
              <Text sx={{ fontWeight: 600 }} variant="body2">
                {formatCurrency(availableBalances - averageMonthlySpend, '0,0.00')}
              </Text>
            </Stack>
          </Card>
        </Stack>
      )}

      <Stack sx={{ p: 24, gap: 8 }}>
        <Text sx={{ fontWeight: 600 }} truncate={false} variant="body1">
          {copy.body_content}
        </Text>
        <BalanceStepper
          amount={formatCurrency(amountToCalculate, '0,0.00')}
          copy={copy}
          onDecrement={() => setAmountToCalculate((prev) => (prev <= 0 ? 0 : prev - deltaAmount))}
          onIncrement={() => setAmountToCalculate((prev) => prev + deltaAmount)}
        />
      </Stack>
      <Stack sx={{ gap: 8 }}>
        <Text sx={{ fontWeight: 600, mx: 24 }} truncate={false} variant="body1">
          {copy.body_description}
        </Text>
        <List sx={{ bgcolor: 'background.paper', width: '100%' }}>
          {monthsTable.map(({ label, value }, index) => {
            const isLastIndex = variant === variants.SAVE_AN_EXTRA_100 ? 2 : 3
            return (
              <React.Fragment key={value}>
                <ListItem
                  disableGutters={true}
                  disablePadding={true}
                  sx={{
                    minHeight: 52,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack sx={{ width: '100%' }}>
                    <Stack
                      sx={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        px: 24,
                        py: 8,
                      }}
                    >
                      <Text truncate={false} variant="body1">
                        {label}
                      </Text>
                      <Text sx={{ fontWeight: 600 }} truncate={false} variant="body1">
                        {formatCurrency(value * amountToCalculate, '0,0')}
                      </Text>
                    </Stack>
                  </Stack>
                </ListItem>
                <Divider component="li" sx={{ ml: index === isLastIndex ? 0 : 24 }} />
              </React.Fragment>
            )
          })}
        </List>
        {variant === variants.SAVE_ENOUGH_TO_LIVE_ON && availableBalances && (
          <Stack sx={{ pt: 6, pb: 14, px: 24, textAlign: 'right', gap: 4 }}>
            <Text truncate={false} variant="h3">
              {formatCurrency(availableBalances, '0,0.00')}
            </Text>
            <Text
              sx={{ color: 'text.secondary', fontWeight: 400 }}
              truncate={false}
              variant="subtitle2"
            >
              {buildDynamicCopy(copy.your_available_balances_text!, date) as string}
            </Text>
          </Stack>
        )}
      </Stack>
    </Drawer>
  )
}
